import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { CAM_FACES, CAM_LEVER, camAt, camCell, camFace, camSet, cellCenter, ventAhead, ventPhase, type BattleState, type CamsRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** The road going down, its flags. */
const ROAD = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 1.5L3 14.5M11 1.5l2 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M4.6 5h6.8M4.1 9h7.8M3.6 13h8.8" stroke="currentColor" stroke-width="1" opacity="0.7"/></svg>';
/** A vent: a grate with flames over it. */
const VENT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13.5h12M3 11h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M5 9c-1-2 1-3 0-6 2 1 2 3 2 4M9.5 9c-1-2 1-3 .3-6 2 1.4 1.7 3.2 1.4 4.4" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** Grillo's head: a round lamp for an eye in a bronze dome. */
const GRILLO = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 11.5V8a5 5 0 0 1 10 0v3.5z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8.2" r="2" fill="currentColor"/><path d="M2 13h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** The trip-hammer: its head on its helve. */
const HAMMER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 5.5l9 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="10" y="5" width="4.5" height="5.5" rx="0.8" fill="currentColor" transform="rotate(18 12 8)"/><path d="M3 14h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** Magma coming up: a spot with a flame. */
const MAGMA = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="12.5" rx="5.5" ry="2" fill="currentColor" opacity="0.6"/><path d="M8 11c-2.4-1.6-1-4-.4-7 1.6 1.4 3.4 3.5 2 6.5" fill="currentColor"/></svg>';
/** The stone's veins: three strokes over a facet. */
const VEINS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l5.6 3.2v6.6L8 14.5l-5.6-3.2V4.7z" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M5 6l3 2 3-3M8 8v4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A cam on its shaft. */
const CAM = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 9h14" stroke="currentColor" stroke-width="1.4"/><path d="M8 3c2.6 0 4 2.4 4 5a4 4 0 0 1-8 0c0-2.6 1.4-5 4-5z" fill="currentColor"/><circle cx="8" cy="9" r="1.3" fill="#0c0e12"/></svg>';
/** La Tenaza's pincer. */
const PINCER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 12c2-1 4-1 5 0M7 12c0-4 2-7 7-8-1 3-3 4-5 4 2 1 3 2 4 4-3 0-5-1-6-2" fill="currentColor"/></svg>';

/** Seconds to a tick, at least 1. */
const secs = (s: BattleState, at: number): number => Math.max(1, Math.ceil((at - s.tick) / TICK_HZ));

type Mark = { el: HTMLButtonElement; label: HTMLElement; state: HTMLElement; slot: number; point: THREE.Vector3 };

/**
 * The heart on screen (docs/sendas/artifice.md, chapter 3), inside a path's own plate (game/senda-hud.ts) and over the
 * board; a phone's plate kept under about a quarter of the screen:
 *
 * - the deep road (`vent`): the way down; the vent ahead of the hero, red while warned or blowing («sopla en 2 s»),
 *   white while quiet with how long it stays so («quieto 4 s: ¡cruzá!»); what Grillo is doing (waiting for the blast,
 *   crossing: follow him, waiting for the hero).
 * - the river of embers (`ember`): the hammer's freeing and why it stops (Dunna takes cover); the magma's next pulse,
 *   red while its spots are marked.
 * - the cams (`cams`, the test): the veins' order as numbered chips (done white, next red), the chips of the stone (of
 *   three); what this turn would strike, in the shaft's order («Esta vuelta pega en: 1 · 5»), or the face going by now;
 *   La Tenaza's state (in the river, after the wheel or the hero, the pincers open, on the wheel, its splash). Over the
 *   board, a mark on each of the six places (its number; «leva» with a cam on it), on each cam on its rack and on the
 *   lever. A tap on a mark sends the hero: what is in its hands and what is there change places, or it lets go.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class CorazonHud {
  readonly panel: HTMLElement;
  private kind: 'vent' | 'ember' | 'cams';
  private v = new THREE.Vector3();
  private key = '';
  private rowA = h('div.bh-row');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow');
  private textC = h('span.al-wave');
  private rowD = h('div.bh-row.al-waverow');
  private textD = h('span.al-wave');
  private bar = h('i.cb-bar');
  private veins: HTMLElement[] = [];
  private chipPips: HTMLElement[] = [];
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.cams ? 'cams' : sr?.ember ? 'ember' : 'vent';
    const rows: HTMLElement[] = [];
    if (this.kind === 'vent') {
      this.rowA.append(h('span.bh-ico', { html: ROAD }), this.textA, h('span.cb-track', {}, this.bar));
      this.rowB.append(h('span.bh-ico', { html: VENT }), this.textB);
      this.rowC.append(h('span.bh-ico', { html: GRILLO }), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
    } else if (this.kind === 'ember') {
      this.rowA.append(h('span.bh-ico', { html: HAMMER }), this.textA, h('span.cb-track', {}, this.bar));
      this.rowB.append(h('span.bh-ico', { html: MAGMA }), this.textB);
      rows.push(this.rowA, this.rowB);
    } else if (sr?.cams) {
      const r = sr.cams;
      this.veins = r.seq.map((f) => h('b.cz-vein', {}, String(f + 1)));
      const order: (HTMLElement | string)[] = [];
      this.veins.forEach((v, i) => {
        if (i) order.push(h('i.cz-arrow', {}, '→'));
        order.push(v);
      });
      this.chipPips = Array.from({ length: r.chips }, () => h('i'));
      this.rowA.classList.add('cz-veins');
      this.rowA.append(h('span.bh-ico', { html: VEINS }), h('span.bh-label', {}, t('senda.heart.veins')), h('span.cz-order', {}, ...order), h('span.al-pips.cb-pips.cz-chips', { title: t('senda.heart.chipsHint') }, ...this.chipPips));
      this.rowB.append(h('span.bh-ico', { html: CAM }), this.textB);
      this.rowD.append(h('span.bh-ico', { html: HAMMER }), this.textD);
      this.rowC.append(h('span.bh-ico', { html: PINCER }), h('span.bh-label', {}, t('senda.heart.boss')), this.textC);
      rows.push(this.rowA, this.rowD, this.rowC);
      this.buildMarks(s, r);
    }
    this.panel = h(`div.bh.tl-panel.cb-panel.cz-panel.cz-${this.kind}`, {}, ...rows);
  }

  /** A mark on each of the six places, each cam's rack spot and the lever. */
  private buildMarks(s: BattleState, r: CamsRules): void {
    const R = s.map.radius;
    for (let slot = 0; slot <= CAM_LEVER; slot++) {
      const c = cellCenter(R, camCell(r, slot));
      const kind = slot < CAM_FACES ? 'axle' : slot === CAM_LEVER ? 'lever' : 'lying';
      const label = h('b.tl-teeth');
      const state = h('small.al-state.tl-state');
      const hint = kind === 'axle' ? 'senda.heart.slotHint' : kind === 'lever' ? 'senda.heart.leverHint' : 'senda.heart.camHint';
      const el = h(
        `button.al-stone.tl-slot.cb-slot.cz-slot.${kind}`,
        { type: 'button', 'data-test': kind === 'lever' ? 'cam-lever' : `cam-slot-${slot}`, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(slot)) },
        h('span.al-ring'),
        label,
        kind === 'lever' ? h('b.mn-name.tl-name', {}, t('senda.heart.lever')) : '',
        state,
      ) as HTMLButtonElement;
      if (kind === 'axle') label.textContent = String(slot + 1);
      this.board.append(el);
      // (The places' marks sit a little in front of the shaft, toward the bank: the plates with their numbers.)
      this.marks.push({ el, label, state, slot, point: simToWorld(c.x, c.y + (kind === 'axle' ? 350 : 0), new THREE.Vector3(), 0.3) });
    }
  }

  private updateVent(s: BattleState): void {
    const r = s.rules?.senda?.vent;
    const w = s.senda?.vent;
    if (!r || !w) return;
    const k = w.end >= 0 ? 100 : Math.floor((Math.min(w.way, r.way.length) * 100) / r.way.length);
    const a = t('senda.heart.road', { n: k });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    setStyle(this.bar, 'width', `${k}%`);
    const hero = s.heroes[0];
    const i = hero ? ventAhead(r, hero.y) : -1;
    const v = i >= 0 ? r.vents[i] : undefined;
    let b = t('senda.heart.noVent');
    let warn = false;
    let good = false;
    if (v) {
      const ph = ventPhase(s, v);
      if (ph < r.blow) {
        b = t('senda.heart.ventNow');
        warn = true;
      } else if (ph >= v.beat - r.warn) {
        b = t('senda.heart.ventIn', { s: Math.max(1, Math.ceil((v.beat - ph) / TICK_HZ)) });
        warn = true;
      } else {
        b = t('senda.heart.ventFree', { s: Math.max(1, Math.ceil((v.beat - r.warn - ph) / TICK_HZ)) });
        good = true;
      }
    }
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('warn', warn);
    this.rowB.classList.toggle('good', good);
    const g = s.senda?.npcs.find((n) => n.id === 'grillo');
    const behind = !!g && !!hero && hero.y < g.y - r.wait + 400;
    const c = w.end >= 0 ? '' : w.cross >= 0 ? t('senda.heart.grilloGoes') : behind ? t('senda.heart.grilloYou') : g && !g.moving ? t('senda.heart.grilloWaits') : t('senda.heart.grilloOn');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('good', w.cross >= 0);
  }

  private updateEmber(s: BattleState): void {
    const r = s.rules?.senda?.ember;
    const w = s.senda?.ember;
    if (!r || !w) return;
    const d = s.senda?.npcs.find((n) => n.id === 'dunna');
    const k = w.end >= 0 ? 100 : Math.floor((w.prog * 100) / r.work);
    const a = w.end >= 0 ? t('senda.heart.free') : d && d.flinch > 0 ? t('senda.heart.dunnaHit') : t('senda.heart.unjam', { n: k });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    setStyle(this.bar, 'width', `${k}%`);
    this.rowA.classList.toggle('warn', !!d && d.flinch > 0 && w.end < 0);
    const on = w.spots.length > 0;
    const b = w.end >= 0 ? '' : on ? t('senda.heart.pulseNow') : t('senda.heart.pulseIn', { s: Math.max(1, Math.ceil((w.pulse - s.waveT) / TICK_HZ)) });
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.hidden = !b;
    this.rowB.classList.toggle('warn', on);
  }

  private updateCams(s: BattleState): void {
    const r = s.rules?.senda?.cams;
    const w = s.senda?.cams;
    if (!r || !w) return;
    this.veins.forEach((v, i) => {
      v.classList.toggle('done', i < w.done);
      v.classList.toggle('next', i === w.done && w.freed < 0);
    });
    this.chipPips.forEach((p, i) => p.classList.toggle('on', i < w.chips));
    this.rowA.classList.toggle('warn', w.chips >= r.chips - 1);
    // What this turn would strike, or the face going by now.
    const set = camSet(w);
    const f = camFace(r, w);
    const d = w.freed >= 0 ? t('senda.heart.freed') : w.tz.mode === 2 ? t('senda.heart.held', { s: secs(s, w.tz.grip) }) : f.face >= 0 ? t('senda.heart.turning', { n: f.face + 1 }) : set.length ? t('senda.heart.preview', { list: set.map((x) => x + 1).join(' · ') }) : t('senda.heart.previewNone');
    const hand = w.freed >= 0 || w.turn >= 0 ? '' : w.hand >= 0 ? t('senda.heart.inHand') : set.length ? t('senda.heart.pull') : t('senda.heart.handFree');
    const dd = hand ? `${d} · ${hand}` : d;
    if (this.textD.textContent !== dd) this.textD.textContent = dd;
    this.rowD.classList.toggle('warn', w.tz.mode === 2 && w.freed < 0);
    this.rowD.classList.toggle('good', w.freed >= 0 || (f.face >= 0 && set.includes(f.face)));
    const tz = w.tz;
    const hero = s.heroes[0];
    const crab = tz.id ? s.enemies.find((e) => e.id === tz.id) : undefined;
    const near = !!hero && !!crab && Math.abs(hero.x - crab.x) + Math.abs(hero.y - crab.y) < r.boss.chase;
    const c =
      tz.mode === 3
        ? t('senda.heart.bossDown')
        : tz.mode === 0
          ? t('senda.heart.inRiver')
          : tz.splashAt >= 0
            ? t('senda.heart.splash', { s: secs(s, tz.splashAt) })
            : tz.wind >= 0
              ? t('senda.heart.snap', { s: secs(s, tz.wind) })
              : tz.mode === 2
                ? t('senda.heart.held', { s: secs(s, tz.grip) })
                : s.tick < tz.again || near
                  ? t('senda.heart.atYou')
                  : t('senda.heart.toWheel');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.classList.toggle('warn', tz.mode !== 3 && (tz.splashAt >= 0 || tz.wind >= 0 || tz.mode === 2));
    const key = `${w.on.join(',')}|${w.hand}|${w.turn >= 0}|${w.freed}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const lever = m.slot === CAM_LEVER;
        const k = lever ? -1 : camAt(w, m.slot);
        const axle = m.slot < CAM_FACES;
        const st = axle ? (k >= 0 ? t('senda.heart.camMark') : '') : lever ? (w.turn >= 0 ? t('senda.heart.turnMark') : set.length ? t('senda.heart.pullMark') : '') : '';
        if (m.state.textContent !== st) {
          m.state.textContent = st;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('full', k >= 0);
        m.el.classList.toggle('ready', lever && set.length > 0 && w.turn < 0);
        // A rack spot with nothing on it shows only with a cam in the hands (somewhere to put it down).
        m.el.classList.toggle('gone', w.freed >= 0 || (!axle && !lever && k < 0 && w.hand < 0));
      }
    }
    for (const m of this.marks) {
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      const need = m.slot === CAM_LEVER ? r.pull : r.stand;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'vent') this.updateVent(s);
    else if (this.kind === 'ember') this.updateEmber(s);
    else this.updateCams(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
